import { computed, makeAutoObservable, observable } from "mobx";
import FabricModel from "../models/FabricModel";
import { swalAlert } from "../utils/Swal";
import { StoreClass } from "./Store";
import ProductModel from "./ProductModel";
import { Gtm } from "../utils/Gtm";
import { Clarity } from "../utils/Clarity";
import { Synerise } from "../utils/Synerise";
import { lang } from "../utils/Lang";
import { apiAdapter } from "../adapters/ApiAdapter";
import { deleteBasketConfiguration, getBasketConfigurations, saveBasketConfiguration, updateBasketConfiguration } from "../utils/api";
import { isFeatureEnabled } from "../utils/Features";

function post(url, data){
    return fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        method: "POST",
        body: JSON.stringify(data)
    });
}
function get(url){
    return fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        method: "GET",
        // body: JSON.stringify(data)
    });
}

/**
 * MYB-121: the shop merges identical basket lines automatically (same product +
 * variant + options -> one line with a summed quantity). To stay in sync we
 * identify a line by a *signature* that survives line-id changes (a forced
 * remove + re-add to lower a shared quantity produces a new line id).
 */
function stableStringify(value) {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value !== 'object') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(',')}]`;
    }
    return `{${Object.keys(value).sort().map((key) => `${key}:${stableStringify(value[key])}`).join(',')}}`;
}

function itemSignature({ productId, variantId, options }) {
    return `${productId ?? ''}:${variantId ?? ''}:${stableStringify(options)}`;
}

/**
 * MYB-121: normalizes the different adapter addToBasket return shapes into a
 * common basket line item structure used for the basket configuration payload.
 * `fallback.payload` is the exact request payload sent to the adapter, stored
 * so the item can be re-added (increment) later.
 */
function normalizeAddedItems(added, fallback = {}) {
    const payload = fallback.payload || {};
    const list = Array.isArray(added) ? added : (added ? [added] : []);
    return list.map((item, index) => {
        const productId = item?.productId ?? item?.product_id ?? payload.product_id ?? fallback.product_id ?? null;
        const variantId = item?.variantId ?? item?.stock_id ?? payload.stock_id ?? fallback.variantId ?? fallback.stock_id ?? null;
        const options = item?.options ?? payload.options ?? fallback.options ?? {};
        return {
            id: item?.id ?? item?.basketItemId ?? item?.cartId ?? null,
            productId,
            variantId,
            options,
            quantity: item?.quantity ?? fallback.quantity ?? 1,
            name: item?.name ?? item?.variant ?? null,
            role: fallback.role ?? null,
            // The response may report the merged line quantity; the stored item
            // always represents a single unit of the bundle (which carries its
            // own `quantity`).
            quantity: 1,
            addPayload: {
                stock_id: variantId,
                options,
                product_id: productId,
            },
            signature: itemSignature({ productId, variantId, options }),
            index,
        };
    });
}

/**
 * MYB-121: the shop can merge identical variants, so two addToBasket calls can
 * return the same basket line id. Keep the first occurrence of each non-null id
 * so the stored configuration never references the same line twice.
 */
function dedupeBasketItems(items = []) {
    const seen = new Set();
    return items.filter((item) => {
        if (item?.id === undefined || item?.id === null) {
            return true;
        }
        const key = String(item.id);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

function contributionOf(item, quantity) {
    return (quantity ?? 1) * (item?.quantity ?? 1);
}

window.adapter = apiAdapter;

export class CartClass{
    store:StoreClass;

    @observable products = [];
    @observable basketConfigurations = [];
    @observable isCartLoading = false;
    @observable basketConfigId = null;

    constructor({store}){
        makeAutoObservable(this);
        this.store = store;
        // MYB-121: configuration ids whose basket line ids changed and need to
        // be persisted again (kept out of the observable graph on purpose).
        this._dirtyConfigs = new Set();
        // MYB-144: debounced persist of a configuration quantity when the main
        // product quantity changes in the basket.
        this._quantityPersistTimer = null;
        this._pendingQuantityPersistIds = new Set();
    }

    /**
     * 20-27 lipca
     */
    @computed get deliveryDate(){
        // return +1day - +7 days date
        var tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        var end = new Date();
        end.setDate(end.getDate() + 7);
        return `${tomorrow.getDate()} - ${end.getDate()} ${end.toLocaleString(LANG_MAP[lang.current] || 'pl-PL', { month: 'long' })} `
    }

    /**
     * MYB-121: the addToBasket payloads for the current configuration (the main
     * product plus every extra product). Used both to add a bundle and to
     * rebuild the desired item set when editing.
     */
    buildBundleRequests(){
        var options = {};
        this.store.product.options_configuration.forEach((option) => {
            if(option.value === null){
                return;
            }
            // if there is no option in values do not add it
            if(!option.options.find((o) => o.valueId === option.value)){
                return;
            }
            options[option.id] = option.value
        });

        const requests = [{
            role: 'main',
            payload: {
                stock_id: this.store.product.stockId,
                options,
                product_id: this.store.product.id,
            },
        }];

        this.store.extraProdcuts.forEach((product) => {
            if(!product.toCartPayload){
                return;
            }
            const payload = product.toCartPayload();
            requests.push({ role: 'extra', payload });
        });

        return requests;
    }

    /**
     * MYB-121: the desired items for the current configuration without touching
     * the basket (ids are resolved by reconciliation). `addPayload` lets an item
     * be re-added later if a shared line has to be reduced.
     */
    buildDesiredItems(){
        return this.buildBundleRequests().map((request, index) => {
            const payload = request.payload || {};
            const productId = payload.product_id;
            const variantId = payload.stock_id;
            const options = payload.options || {};
            return {
                id: null,
                productId,
                variantId,
                options,
                quantity: 1,
                role: request.role,
                addPayload: {
                    stock_id: variantId,
                    options,
                    product_id: productId,
                },
                signature: itemSignature({ productId, variantId, options }),
                index,
            };
        });
    }

    /**
     * Adds the configured bed (main product + extra products) to the basket.
     * Returns the normalized basket line items that were created.
     */
    async addConfigurationItems(){
        const basketItems = [];

        for (const request of this.buildBundleRequests()) {
            console.log('Adding to cart', request.payload);
            const added = await apiAdapter.addToBasket(request.payload);
            basketItems.push(...normalizeAddedItems(added, {
                role: request.role,
                payload: request.payload,
            }));
        }

        return dedupeBasketItems(basketItems);
    }

    /**
     * MYB-121: deep-equality check (product + steps + extra) used to merge an
     * identical configuration into the existing bundle instead of creating a
     * duplicate record that only differs by id.
     */
    findMatchingConfiguration(configuration){
        if(!configuration){
            return null;
        }
        const key = `${configuration.productId}:${stableStringify({ steps: configuration.steps, extra: configuration.extra })}`;
        return this.basketConfigurations.find((config) => {
            const other = config.configuration || {};
            const otherKey = `${config.productId ?? other.productId}:${stableStringify({ steps: other.steps, extra: other.extra })}`;
            return otherKey === key;
        }) || null;
    }

    /**
     * MYB-121: the PDP url the configurator should return to when editing.
     */
    getReturnUrl(){
        const productUrl = this.store.product?.url;
        if(productUrl){
            try {
                return new URL(productUrl, window.location.origin).toString();
            } catch (err) {
                console.error('[Cart] invalid product url', productUrl, err);
            }
        }
        const u = new URL(window.location.href);
        u.searchParams.delete('basketConfig');
        return u.toString();
    }

    /**
     * MYB-121: persists the current configuration together with the created
     * basket line items (separate from the user save/share configuration).
     */
    async persistBasketConfiguration(basketItems, quantity = 1){
        if(!isFeatureEnabled()){
            return null;
        }
        try {
            const response = await saveBasketConfiguration({
                productId: this.store.product.id,
                returnUrl: this.getReturnUrl(),
                lang: lang.current,
                quantity,
                configuration: this.store.saveProject.buildConfiguration(),
                basketItems,
            });
            if(response?.id){
                this.basketConfigId = response.id;
            }
            return response;
        } catch (err) {
            console.error('[Cart] saveBasketConfiguration failed', err);
            return null;
        }
    }

    /**
     * MYB-121: persists a bundle that already exists (quantity change while
     * adding, or edited line ids after reconciliation).
     */
    async persistExistingConfiguration(config, previousBasketItems = null){
        if(!isFeatureEnabled() || !config?.id){
            return null;
        }
        const previous = previousBasketItems || this.uniqueItemIds(config.basketItems);
        try {
            const response = await updateBasketConfiguration({
                id: config.id,
                productId: config.productId ?? this.store.product.id,
                returnUrl: config.returnUrl ?? this.getReturnUrl(),
                lang: config.lang ?? lang.current,
                quantity: config.quantity ?? 1,
                configuration: config.configuration,
                basketItems: config.basketItems,
                previousBasketItems: previous,
            });
            return response;
        } catch (err) {
            console.error('[Cart] updateBasketConfiguration failed', err);
            return null;
        }
    }

    uniqueItemIds(items = []){
        return Array.from(new Map(
            (items || [])
                .filter((item) => item?.id !== undefined && item?.id !== null)
                .map((item) => [String(item.id), item.id])
        ).values());
    }

    /**
     * Adds whole configuration to cart
     */
    async addToCart(){
        try {
            const configuration = this.store.saveProject.buildConfiguration();
            const existing = isFeatureEnabled()
                ? this.findMatchingConfiguration(configuration)
                : null;
            const basketItems = await this.addConfigurationItems();

            if(existing){
                // Same configuration again -> the shop merged the lines and
                // bumped their quantity; track it as a quantity increment on the
                // existing bundle instead of creating a duplicate record.
                existing.quantity = (existing.quantity ?? 1) + 1;
                existing.basketItems = basketItems;
                await this.persistExistingConfiguration(existing);
                this.basketConfigId = existing.id;
                await this.reconcileBasket();
            } else {
                const response = await this.persistBasketConfiguration(basketItems, 1);
                const record = response?.basketConfiguration || (response?.id ? {
                    id: response.id,
                    productId: this.store.product.id,
                    returnUrl: this.getReturnUrl(),
                    lang: lang.current,
                    quantity: 1,
                    configuration,
                    basketItems,
                } : null);
                if(record){
                    this.basketConfigurations.push(record);
                }
                await this.reconcileBasket();
            }

            // store.openCartSummaryPopup();
            this.store.openCartSummaryPopup();

            Gtm.setStore(this.store);
            Gtm.push({
                event: "add_to_cart",
                ecommerce: {
                    konfigurator: "tak",
                    items: [
                        {
                            item_name: Gtm.getProductName(),
                            item_id: Gtm.getProductId(),
                            item_brand: "MyBed",
                            item_category: "Łóżka",
                            item_variant: Gtm.getVariantStr(),
                        }
                    ]
                }
            });
            Clarity.push("add_to_cart");

            Synerise.setStore(this.store);
            Synerise.push('product.addToCart', {
                configurator: true,
                configuratorId: this.basketConfigId || this.store.saveProject.hash || undefined,
                ...Synerise.getConfiguratorParams()
            });
            Synerise.push('cart.status', {
                value: this.totalPrice
            });
    
        } catch (err) {
            console.error(err)
            swalAlert({
                title: "Błąd",
                html:
                    err.toString(),
                    // err.toString() +
                    // `<pre style="font-size: 9px; line-height: 1.2; background-color: #eee; padding: 10px; word-break: break-all;white-space: pre-wrap;">${err.stack}</pre>`,
                
            })
        }
    }

    /**
     * MYB-121: replaces the basket line items that belong to the currently
     * edited bundle with the freshly configured ones. Every other bundle is kept
     * in sync through reconciliation (shared lines are only decremented).
     */
    async updateBasket(){
        if(!isFeatureEnabled() || !this.basketConfigId){
            return this.addToCart();
        }
        try {
            const config = this.basketConfigurations.find((c) => c.id === this.basketConfigId);
            if(!config){
                return this.addToCart();
            }
            const previousBasketItems = this.uniqueItemIds(config.basketItems);
            const oldItems = config.basketItems || [];

            this._dirtyConfigs.clear();
            config.configuration = this.store.saveProject.buildConfiguration();
            // Reuse line ids of items whose signature did not change so an edit
            // only touches the lines that actually differ.
            config.basketItems = this.buildDesiredItems().map((item) => {
                const old = oldItems.find((it) => (it.signature || itemSignature(it)) === item.signature);
                const match = old || this.findItemBySignature(item.signature, { excludeConfigId: config.id });
                return match && match.id !== undefined && match.id !== null
                    ? { ...item, id: match.id }
                    : item;
            });

            const signatureById = new Map();
            oldItems.forEach((item) => {
                if(item.id === undefined || item.id === null){
                    return;
                }
                const key = String(item.id);
                if(!signatureById.has(key)){
                    signatureById.set(key, new Set());
                }
                signatureById.get(key).add(item.signature || itemSignature(item));
            });

            // Decrement/remove lines this bundle no longer uses, then drop the
            // ones that are no longer owned by any bundle.
            await this.reconcileBasket();
            await this.pruneOrphanLines(previousBasketItems, signatureById);
            await this.persistExistingConfiguration(config, previousBasketItems);
            await this.loadCart();
            this.store.openCartSummaryPopup();
            return { status: true, id: config.id };
        } catch (err) {
            console.error(err)
            swalAlert({
                title: "Błąd",
                html: err.toString(),
            })
        }
    }

    /**
     * MYB-121: the first stored item that matches a signature (optionally
     * ignoring the bundle being edited).
     */
    findItemBySignature(signature, { excludeConfigId = null } = {}){
        for (const config of this.basketConfigurations) {
            if(excludeConfigId && config.id === excludeConfigId){
                continue;
            }
            const item = (config.basketItems || []).find((it) => (it.signature || itemSignature(it)) === signature);
            if(item){
                return item;
            }
        }
        return null;
    }

    /**
     * MYB-121: maps every line signature to the bundles that own it. The shop
     * merges identical lines, so a line can be owned by several bundles.
     */
    buildOwnership(){
        const owners = new Map();
        this.basketConfigurations.forEach((config) => {
            const quantity = config.quantity ?? 1;
            (config.basketItems || []).forEach((item) => {
                const signature = item.signature || itemSignature(item);
                if(!owners.has(signature)){
                    owners.set(signature, []);
                }
                owners.get(signature).push({
                    config,
                    item,
                    contribution: contributionOf(item, quantity),
                });
            });
        });
        return owners;
    }

    /**
     * MYB-144: the main product quantity is the bundle count. The shop's basket
     * recalc form submits every `quantity_<lineId>` input in one request, so
     * instead of calling the add/remove API we compute the desired quantity of
     * every linked line (main + extras, including lines shared by several
     * bundles) and let the theme's form submit them together. The changed
     * configuration's `quantity` is persisted so the stored basket-configuration
     * stays in sync.
     *
     * Pure computation (no basket mutation): returns a Map of
     * `lineId (string) -> quantity (number)`.
     */
    planQuantitiesForMain(lineId, newTotal, { persist = true } = {}){
        const quantity = Math.max(1, parseInt(newTotal, 10) || 1);
        const itemsOf = (config) => config.basketItems || [];
        const owners = this.basketConfigurations.filter((config) =>
            itemsOf(config).some((item) => String(item.id) === String(lineId))
        );
        if(owners.length === 0){
            return new Map();
        }

        const primary = owners.find((config) =>
            itemsOf(config).some((item) => item.role === 'main' && String(item.id) === String(lineId))
        ) || owners[0];

        const primaryItem = itemsOf(primary).find((item) => item.role === 'main' && String(item.id) === String(lineId))
            || itemsOf(primary).find((item) => String(item.id) === String(lineId));
        const signature = primaryItem ? (primaryItem.signature || itemSignature(primaryItem)) : null;

        // Bundles that share the same main line contribute to its total.
        let othersContribution = 0;
        if(signature){
            owners.forEach((config) => {
                if(config === primary){
                    return;
                }
                itemsOf(config).forEach((item) => {
                    if((item.signature || itemSignature(item)) === signature){
                        othersContribution += contributionOf(item, config.quantity);
                    }
                });
            });
        }

        primary.quantity = Math.max(1, quantity - othersContribution);

        // Desired quantity per line, summed across every owner.
        const desired = new Map();
        this.buildOwnership().forEach((entries) => {
            const total = entries.reduce((sum, entry) => sum + entry.contribution, 0);
            const withId = entries.find((entry) => entry.item?.id !== undefined && entry.item?.id !== null);
            if(withId && total > 0){
                desired.set(String(withId.item.id), total);
            }
        });

        if(persist){
            this.scheduleQuantityPersist(primary);
        }

        return desired;
    }

    /**
     * MYB-144: persists the changed configuration's quantity shortly after the
     * input settles, before the theme's debounced basket recalc reloads the page.
     */
    scheduleQuantityPersist(config){
        if(!config?.id){
            return;
        }
        this._pendingQuantityPersistIds.add(config.id);
        clearTimeout(this._quantityPersistTimer);
        this._quantityPersistTimer = setTimeout(() => {
            const ids = Array.from(this._pendingQuantityPersistIds);
            this._pendingQuantityPersistIds.clear();
            ids.forEach((id) => {
                const current = this.basketConfigurations.find((c) => c.id === id);
                if(current){
                    this.persistExistingConfiguration(current);
                }
            });
        }, 250);
    }

    /**
     * MYB-121: brings the basket in line with the sum of all bundle
     * contributions using add/remove only. A line whose target quantity is lower
     * than the current one is removed and re-added (the shop has no decrement
     * API); every record referencing it is updated with the new line id.
     */
    async reconcileBasket(){
        if(!isFeatureEnabled()){
            return;
        }
        // Some front ends ignore the quantity field on addToBasket, so allow a
        // second pass to top up any line that is still short.
        for (let pass = 0; pass < 2; pass += 1) {
            const changed = await this.reconcilePass();
            if(!changed){
                return;
            }
        }
    }

    async reconcilePass(){
        const ownership = this.buildOwnership();
        const desired = new Map();
        ownership.forEach((entries, signature) => {
            desired.set(signature, entries.reduce((sum, entry) => sum + entry.contribution, 0));
        });

        const cart = await this.getCart();
        const quantityById = new Map(
            (cart?.products || []).map((product) => [String(product.id), product.quantity ?? 1])
        );

        let changed = false;
        for (const [signature, target] of desired) {
            const entries = ownership.get(signature) || [];
            const known = entries.find((entry) => entry.item?.id !== undefined && entry.item?.id !== null);
            const lineId = known ? known.item.id : null;
            const current = lineId !== null && quantityById.has(String(lineId))
                ? quantityById.get(String(lineId))
                : 0;
            if(current === target){
                continue;
            }
            changed = true;

            const payloadEntry = entries.find((entry) =>
                entry.item?.addPayload?.stock_id !== undefined && entry.item?.addPayload?.stock_id !== null
            );
            const addPayload = payloadEntry ? payloadEntry.item.addPayload : null;

            let newId = lineId;
            if(target === 0){
                if(current > 0 && lineId !== null){
                    await apiAdapter.removeFromBasket(lineId);
                }
                newId = null;
            } else if(current === 0){
                newId = await this.addUnits(addPayload, target, null);
            } else if(target > current){
                newId = await this.addUnits(addPayload, target - current, lineId);
            } else {
                await apiAdapter.removeFromBasket(lineId);
                newId = await this.addUnits(addPayload, target, null);
            }

            if(newId !== lineId){
                this.updateRecordsLineId(signature, newId);
            }
        }

        if(changed){
            await this.persistDirtyConfigurations();
        }
        return changed;
    }

    async addUnits(addPayload, quantity, existingLineId = null){
        if(!addPayload || addPayload.stock_id === undefined || addPayload.stock_id === null){
            console.warn('[Cart] missing add payload, cannot add units', addPayload);
            return existingLineId;
        }
        let lineId = existingLineId;
        for (let i = 0; i < quantity; i += 1) {
            const added = await apiAdapter.addToBasket({ ...addPayload });
            const items = normalizeAddedItems(added, { payload: addPayload });
            const withId = items.find((item) => item.id !== undefined && item.id !== null);
            if(withId){
                lineId = withId.id;
            }
        }
        return lineId;
    }

    updateRecordsLineId(signature, lineId){
        this.basketConfigurations.forEach((config) => {
            let touched = false;
            (config.basketItems || []).forEach((item) => {
                if((item.signature || itemSignature(item)) === signature && item.id !== lineId){
                    item.id = lineId;
                    touched = true;
                }
            });
            if(touched){
                this._dirtyConfigs.add(config.id);
            }
        });
    }

    async persistDirtyConfigurations(){
        const ids = Array.from(this._dirtyConfigs);
        this._dirtyConfigs.clear();
        for (const id of ids) {
            const config = this.basketConfigurations.find((c) => c.id === id);
            if(!config){
                continue;
            }
            await this.persistExistingConfiguration(config);
        }
    }

    /**
     * MYB-121: a basket line was removed. Delete every bundle that owns the line
     * (the shop merges shared lines, so a removed line invalidates all of them),
     * then reconcile to decrement items still owned by surviving bundles.
     */
    async removeConfigurationsForLine(lineId, productId = null){
        if(!isFeatureEnabled() || lineId === undefined || lineId === null){
            return false;
        }
        // Refresh from the API first so every configuration linked to the
        // current basket is present (the in-memory list can be incomplete).
        try {
            await this.loadCart();
        } catch (err) {
            console.error('[Cart] loadCart before removal failed', err);
        }
        const ownerIds = new Set();
        const signatures = new Set();
        // Direct owners of the line, plus the signatures of the matching items.
        this.basketConfigurations.forEach((config) => {
            (config.basketItems || []).forEach((item) => {
                if(String(item.id) === String(lineId)){
                    ownerIds.add(config.id);
                    signatures.add(item.signature || itemSignature(item));
                }
            });
        });
        // A merged line can be missing/stale on one of its owners (the shop's
        // add response does not always echo the merged line id), so also include
        // every bundle that references any of those signatures.
        if(signatures.size > 0){
            this.basketConfigurations.forEach((config) => {
                (config.basketItems || []).forEach((item) => {
                    if(signatures.has(item.signature || itemSignature(item))){
                        ownerIds.add(config.id);
                    }
                });
            });
        }
        // Last resort: a main product that matches the clicked row but whose line
        // id is missing on every bundle.
        if(ownerIds.size === 0 && productId !== undefined && productId !== null && productId !== ''){
            this.basketConfigurations.forEach((config) => {
                const isMain = (config.basketItems || []).some((item) =>
                    item.role === 'main' && String(item.productId) === String(productId)
                );
                if(isMain){
                    ownerIds.add(config.id);
                }
            });
        }
        if(ownerIds.size === 0){
            return false;
        }
        const owners = this.basketConfigurations.filter((config) => ownerIds.has(config.id));
        if(owners.length === 0){
            return false;
        }
        const ids = owners.map((config) => config.id);
        const ownedLineIds = new Set();
        const signatureById = new Map();
        owners.forEach((config) => (config.basketItems || []).forEach((item) => {
            const signature = item.signature || itemSignature(item);
            if(item.id !== undefined && item.id !== null){
                const key = String(item.id);
                ownedLineIds.add(key);
                if(!signatureById.has(key)){
                    signatureById.set(key, new Set());
                }
                signatureById.get(key).add(signature);
            }
        }));
        // Always candidate the clicked line itself.
        ownedLineIds.add(String(lineId));

        this._dirtyConfigs.clear();
        // A merged line id can be missing on some bundles; propagate the known id
        // to every bundle sharing the signature before removing the owners, so a
        // surviving bundle keeps a valid line id.
        owners.forEach((config) => (config.basketItems || []).forEach((item) => {
            if(item.id !== undefined && item.id !== null){
                this.updateRecordsLineId(item.signature || itemSignature(item), item.id);
            }
        }));

        this.basketConfigurations = this.basketConfigurations.filter((config) => !ids.includes(config.id));
        // Shared lines are only decremented; exclusive ones are pruned below.
        await this.reconcileBasket();
        await this.persistDirtyConfigurations();
        await this.pruneOrphanLines(Array.from(ownedLineIds), signatureById);
        for (const id of ids) {
            try {
                await deleteBasketConfiguration(id);
            } catch (err) {
                console.error('[Cart] deleteBasketConfiguration failed', id, err);
            }
        }
        await this.loadCart();
        return true;
    }

    /**
     * MYB-121: removes basket lines that were owned by a removed/edited bundle
     * and are not referenced by any remaining bundle. Shared lines are left
     * alone (reconcile already sized them for their surviving owners).
     */
    async pruneOrphanLines(candidateIds, signatureById = new Map()){
        const candidates = new Set((candidateIds || []).map((id) => String(id)));
        if(candidates.size === 0){
            return;
        }
        const remainingOwned = new Set();
        const remainingSignatures = new Set();
        this.basketConfigurations.forEach((config) => (config.basketItems || []).forEach((item) => {
            if(item.id !== undefined && item.id !== null){
                remainingOwned.add(String(item.id));
            }
            remainingSignatures.add(item.signature || itemSignature(item));
        }));

        const cart = await this.getCart();
        const liveIds = new Set((cart?.products || []).map((product) => String(product.id)));
        for (const lineId of candidates) {
            if(remainingOwned.has(lineId) || !liveIds.has(lineId)){
                continue;
            }
            // A surviving bundle may still own this line by signature even if it
            // did not carry the line id (the shop does not always echo it).
            const signatures = signatureById.get(lineId);
            const stillOwned = signatures
                ? Array.from(signatures).some((signature) => remainingSignatures.has(signature))
                : false;
            if(stillOwned){
                continue;
            }
            try {
                await apiAdapter.removeFromBasket(lineId);
            } catch (err) {
                console.error('[Cart] prune removeFromBasket failed', lineId, err);
            }
        }
    }

    /**
     * Adds whole configuration to cart
     */
    async addProductToCart(product:ProductModel){
        try {
            var options = {};
            product.options_configuration.forEach((option) => {
                options[option.id] = option.value
            });
            
            
            console.log({
                stock_id: product.stockId,
                options: options
            })
            const response = await apiAdapter.addToBasket({
                stock_id: product.stockId,
                options: options,
                quantity: product.quantity,
                product_id: product.id
            });

            if(response.length > 0){
                product.cartId = response[0].id;
            }

            product.inCart = true;
            Gtm.push({
                event: "add_to_cart",
                ecommerce: {
                    items: [
                        {
                            item_name: product.name,
                            item_id: String(product.id),
                            item_brand: product.brandName || "",
                            item_category: product.categoryName || "Niewidoczne",
                            item_variant: "",
                            item_list_name: "detailview",
                            index: null,
                            price: product.price,
                            currency: "PLN",
                            quantity: product.quantity
                        }
                    ],
                    currency: "PLN",
                    value: product.priceSum
                }
            });
            Clarity.push("add_to_cart");
            
        } catch (err) {
            console.error(err)
            swalAlert({
                title: "Błąd",
                html:
                    err.toString(),
                    // err.toString() +
                    // `<pre style="font-size: 9px; line-height: 1.2; background-color: #eee; padding: 10px; word-break: break-all;white-space: pre-wrap;">${err.stack}</pre>`,
                
            })
        }
    }

    /**
     * 
     * @param {FabricModel} swatch 
     */
    async addSwatch(swatch){

        try {
            const s = this.store.sampleFabricProdcut.clone();

            var options = {};
            s.options_configuration.forEach((option) => {
                options[option.id] = swatch.valueId
            });
            // this.store.sortedStepsFlat.forEach((step) => {
                
            // })
            console.log({
                stock_id: s.stockId,
                options: options
            })
            const response = await apiAdapter.addToBasket({
                stock_id: s.stockId,
                options: options,
                product_id: s.id
            });
            return response;
        } catch (err) {
            console.error(err)
            swalAlert({
                title: "Błąd",
                html:
                    err.toString(),
                    // err.toString() +
                    // `<pre style="font-size: 9px; line-height: 1.2; background-color: #eee; padding: 10px; word-break: break-all;white-space: pre-wrap;">${err.stack}</pre>`,
                
            })
        }
    }

    /**
     * 
     * @param {FabricModel} swatch 
     */
    async removeSwatch(swatch){

        try {
            if(!swatch.cartId){
             throw Error('cartId is not defined');
            }
           const response = await apiAdapter.removeFromBasket(swatch.cartId);
        
           return response;
        } catch (err) {
            console.error(err)
            swalAlert({
                title: "Błąd",
                html:
                    err.toString(),
                    // err.toString() +
                    // `<pre style="font-size: 9px; line-height: 1.2; background-color: #eee; padding: 10px; word-break: break-all;white-space: pre-wrap;">${err.stack}</pre>`,
                
            })
        }
    }

    async getCart(){
        return apiAdapter.getBasket();
    }

    /**
     * MYB-121: fetches the entire basket and resolves the basket configurations
     * linked to its line items. Called on page load and after add/update.
     */
    async loadCart(){
        this.isCartLoading = true;
        try {
            const response = await this.getCart();
            const products = response?.products || [];
            this.products = products;

            if(products.length > 0){
                var swatches = products.filter((product) => {
                    return product.product_id === this.store.sampleFabricProdcut?.id;
                });
                console.log({swatches});
                if(swatches.length > 0){
                    this.store.selectedSwatches.fillFromStore(swatches);
                }
            }

            if(isFeatureEnabled()){
                if(products.length > 0){
                    const configResponse = await getBasketConfigurations({
                        basketItemIds: products.map((product) => product.id)
                    });
                    this.basketConfigurations = configResponse?.basketConfigurations || [];
                } else {
                    this.basketConfigurations = [];
                }
                // Keep the stored configurations in sync with the basket: drop
                // anything that is no longer linked to a live line item.
                await this.cleanupOrphanConfigurations(products);
            } else {
                this.basketConfigurations = [];
            }

            return response;
        } catch (err) {
            console.error('[Cart] loadCart failed', err);
            return null;
        } finally {
            this.isCartLoading = false;
        }
    }

    /**
     * MYB-121: removes stored configurations that are not linked to any current
     * basket line item (stale/orphaned records left behind when lines were
     * removed outside the configurator). Fetches all configurations and deletes
     * the unlinked ones.
     */
    async cleanupOrphanConfigurations(products = []){
        try {
            const allResponse = await getBasketConfigurations({ basketItemIds: [] });
            const all = allResponse?.basketConfigurations || [];
            if(all.length === 0){
                return;
            }
            const liveIds = new Set((products || []).map((product) => String(product.id)));
            for (const config of all) {
                const items = config.basketItems || [];
                const linked = items.some((item) =>
                    item.id !== undefined && item.id !== null && liveIds.has(String(item.id))
                );
                if(linked || items.length === 0){
                    continue;
                }
                // Only treat it as orphaned when every item id is known but none
                // is live; a config with unresolved (null) ids could still be
                // valid (the shop did not echo the merged line id).
                const hasUnknownId = items.some((item) =>
                    item.id === undefined || item.id === null
                );
                if(hasUnknownId){
                    continue;
                }
                try {
                    await deleteBasketConfiguration(config.id);
                    console.log('[Cart] removed orphaned basket configuration', config.id);
                } catch (err) {
                    console.error('[Cart] failed to delete orphaned configuration', config.id, err);
                }
            }
        } catch (err) {
            console.error('[Cart] cleanupOrphanConfigurations failed', err);
        }
    }

    clear(){
        
        this.store.selectedSwatches.clear();
    }

    async getProduct(productId){
       return apiAdapter.getProduct(productId);
    }
    



    @computed get totalPrice(){
        var sum = 0;
        console.groupCollapsed('totalPrice');
        if(this.store.product?.price){
            sum += this.store.product.price;
            console.log({
                price: this.store.product.price,
                name: this.store.product.name
            });
        }
        // this.store.product?.options_configuration.forEach((option) => {
        //     var p = option.selectedOption?.price;
        //     // if(isNaN(p) && !isNaN(option?.step?.selectedOption?.productPrice)){
        //     //    p = option.step.selectedOption.productPrice; 
        //     // } 
        //     if(!isNaN(p)){
        //         sum += p;
        //     }
            

        //     console.log({
        //         price: p,
        //         optionName: option.name,
        //         option,
        //     });
        // });
        // extra options
        sum += this.extraProductsPrices;
        console.groupEnd();
        return sum;
    }

    @computed get extraProductsPrices(){
        var sum = 0;
        this.store.extraProdcuts.forEach((product) => {
            // MYB-144: mattresses bought in a set with the bed are discounted.
            sum += product.discountedPrice ?? product.price;
        });
        return sum;
    }
}
